import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogContent, DialogTitle,
  IconButton, MenuItem, Stack, Table, TableBody, TableCell, TableRow, TextField, Typography, alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { PamGrid } from '@1823-partners/core';
import StatusCards from './StatusCards';
import StatusChip from './StatusChip';
import { CASH_TOL, STATUS_CASH, STATUS_CASH_ORDER } from '../../utils/derivativeReconConstants';
import { extractEntity, fmt, formatCell, getVal, stripSuffix } from '../../utils/derivativeReconFormat';
import { runDerivativeCashRec } from '../../api/derivativeRecon';

const CW_TX_ID = 'Identifier';

function classifyCustody(f) {
  const n = f.name.toLowerCase();
  if (n.includes('cash_activity') || n.includes('northern')) return 'nt';
  if (n.includes('all transaction') || n.includes('jpm')) return 'jpm';
  return null;
}

function CashDetailDialog({ selected, cwTxRows, onClose }) {
  const cwRows = useMemo(() => {
    if (!selected?.mil?.tradeIds?.length) return [];
    const ids = new Set(selected.mil.tradeIds.map((id) => String(id).trim()));
    return (cwTxRows || []).filter((r) => {
      const raw = String(getVal(r, CW_TX_ID) || '').trim();
      return ids.has(stripSuffix(raw));
    });
  }, [selected, cwTxRows]);

  if (!selected) return null;
  const c1 = selected.custody && !Array.isArray(selected.custody) ? selected.custody : null;
  const ambiguous = selected.ambiguous;

  const rows = [
    ['Type', selected.mil?.type || '—'],
    ['Broker', selected.mil?.broker || '—'],
    ['Entity', selected.mil ? extractEntity(selected.mil.entity) : '—'],
    ['Milliman Date', selected.mil?.dateStr || '—'],
    ['Milliman Amount', selected.mil ? fmt(Math.abs(selected.mil.total)) : '—'],
    ['Custody Date', ambiguous ? selected.custody.map((c) => c.dateStr).join(' / ') : c1?.dateStr || '—'],
    ['Custody Amount', ambiguous ? selected.custody.map((c) => fmt(Math.abs(c.amt))).join(' / ') : c1 ? fmt(Math.abs(c1.amt)) : '—'],
    ['Source', ambiguous ? selected.custody.map((c) => c.source).join(' / ') : c1?.source || '—'],
    ['Description', ambiguous ? selected.custody.map((c) => c.desc).join(' | ') : c1?.desc || '—'],
    ['Difference', selected.diff != null ? fmt(selected.diff) : '—'],
    ['Status', <StatusChip key="st" status={selected.status} cash />],
    ['Contributing Trade IDs', selected.mil?.tradeIds?.length ? (
      <Stack direction="row" flexWrap="wrap" gap={0.5} key="tids">
        {selected.mil.tradeIds.map((id) => <Chip key={id} size="small" label={id} sx={{ fontFamily: 'monospace', fontSize: 11 }} />)}
      </Stack>
    ) : '—'],
  ];

  return (
    <Dialog open={!!selected} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1" fontWeight={800}>Cash Rec Detail</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider', mb: cwRows.length ? 2 : 0 }}>
          <Table size="small">
            <TableBody>
              {rows.map((row, i) => (
                <TableRow key={row[0]} sx={{ bgcolor: i % 2 ? 'action.hover' : 'transparent' }}>
                  <TableCell sx={{ width: 200, color: 'text.secondary', fontWeight: 600, fontSize: 12 }}>{row[0]}</TableCell>
                  <TableCell sx={{ color: 'text.primary', fontSize: 12 }}>{row[1]}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        {cwRows.length > 0 && (
          <Box>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>CW Transactions ({cwRows.length} rows)</Typography>
            <Box sx={{ maxHeight: 240, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableBody>
                  <TableRow>
                    {Object.keys(cwRows[0]).map((c) => <TableCell key={c} sx={{ bgcolor: 'background.default', fontWeight: 700, fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap' }}>{c}</TableCell>)}
                  </TableRow>
                  {cwRows.map((r, i) => (
                    <TableRow key={i}>
                      {Object.keys(cwRows[0]).map((c) => <TableCell key={c} sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>{formatCell(c, r[c])}</TableCell>)}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function CashRecView({ flows, cwTxRows, initialResults, onResults }) {
  const [ntFile, setNtFile] = useState(null);
  const [jpmFile, setJpmFile] = useState(null);
  const [results, setResults] = useState(initialResults || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterType, setFilterType] = useState('All');
  const [search, setSearch] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files).filter((f) => f.name.match(/\.(xlsx?|csv)$/i));
    const unmatched = [];
    dropped.forEach((f) => {
      const k = classifyCustody(f);
      if (k === 'nt') setNtFile(f);
      else if (k === 'jpm') setJpmFile(f);
      else unmatched.push(f.name);
    });
    setError(unmatched.length ? `Could not classify: ${unmatched.join(', ')}` : null);
  }, []);

  const onRunCashRec = useCallback(async () => {
    if (!ntFile && !jpmFile) { setError('Upload at least one custody file.'); return; }
    setLoading(true);
    setError(null);
    try {
      const resp = await runDerivativeCashRec({ flows, ntFile, jpmFile });
      const cashResults = resp.cashResults || [];
      setResults(cashResults);
      if (onResults) {
        onResults({
          cashResults,
          wires: resp.wires || {},
          tradeWireIndex: resp.tradeWireIndex || {},
        });
      }
    } catch (ex) {
      setError(ex.message || String(ex));
    }
    setLoading(false);
  }, [ntFile, jpmFile, flows, onResults]);

  const filtered = useMemo(() => {
    if (!results) return [];
    let r = results;
    if (filterStatus) r = r.filter((x) => x.status === filterStatus);
    if (filterType !== 'All') r = r.filter((x) => x.mil && x.mil.type === filterType);
    if (search) {
      const s = search.toLowerCase();
      r = r.filter(
        (x) =>
          (x.mil ? (x.mil.broker || '').toLowerCase() : '').includes(s) ||
          (x.custody && !Array.isArray(x.custody) ? (x.custody.desc || '').toLowerCase() : '').includes(s),
      );
    }
    return r;
  }, [results, filterStatus, filterType, search]);

  const counts = useMemo(() => {
    const c = {};
    Object.keys(STATUS_CASH).forEach((s) => { c[s] = 0; });
    (results || []).forEach((r) => { if (c[r.status] !== undefined) c[r.status]++; });
    return c;
  }, [results]);

  const columnDefs = useMemo(
    () => [
      { field: 'type', headerName: 'Type', width: 110, valueGetter: (p) => p.data.mil?.type, cellRenderer: (p) => p.value ? <Chip size="small" label={p.value} sx={{ bgcolor: p.value === 'Premium' ? alpha('#38bdf8', 0.18) : alpha('#fbbf24', 0.18), color: p.value === 'Premium' ? '#38bdf8' : '#fbbf24', fontWeight: 600, fontSize: 11 }} /> : '—' },
      { headerName: 'Broker', width: 140, valueGetter: (p) => p.data.mil?.broker || '—' },
      { headerName: 'Entity', width: 100, valueGetter: (p) => p.data.mil ? extractEntity(p.data.mil.entity) : '—' },
      { headerName: 'Mil Date', width: 110, valueGetter: (p) => p.data.mil?.dateStr || '—' },
      { headerName: 'Mil Amount', width: 130, type: 'numericColumn', valueGetter: (p) => p.data.mil ? Math.abs(p.data.mil.total) : null, valueFormatter: (p) => fmt(p.value) },
      { headerName: 'Custody Date', width: 130, valueGetter: (p) => { if (p.data.ambiguous) return p.data.custody.map((c) => c.dateStr).join(' / '); return p.data.custody && !Array.isArray(p.data.custody) ? p.data.custody.dateStr : '—'; } },
      { headerName: 'Custody Amount', width: 150, type: 'numericColumn', valueGetter: (p) => { if (p.data.ambiguous) return p.data.custody.map((c) => fmt(Math.abs(c.amt))).join(' / '); return p.data.custody && !Array.isArray(p.data.custody) ? fmt(Math.abs(p.data.custody.amt)) : '—'; } },
      { headerName: 'Source', width: 100, valueGetter: (p) => { if (p.data.ambiguous) return p.data.custody.map((c) => c.source).join('/'); return p.data.custody && !Array.isArray(p.data.custody) ? p.data.custody.source : '—'; } },
      { headerName: 'Description', flex: 1, minWidth: 200, valueGetter: (p) => { if (p.data.ambiguous) return p.data.custody.map((c) => c.desc).join(' | '); return p.data.custody && !Array.isArray(p.data.custody) ? p.data.custody.desc : '—'; } },
      { field: 'diff', headerName: 'Diff ($)', width: 110, type: 'numericColumn', valueFormatter: (p) => p.value != null ? fmt(p.value) : '—', cellStyle: (p) => p.value == null ? { color: '#94a3b8' } : { color: Math.abs(p.value) <= CASH_TOL ? '#34d399' : '#f87171', fontWeight: 700 } },
      { field: 'status', headerName: 'Status', width: 200, cellRenderer: (p) => <StatusChip status={p.value} cash /> },
    ],
    [],
  );

  if (!results) {
    return (
      <Box sx={{ maxWidth: 600 }}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Upload custody files to reconcile against Milliman's aggregated premiums and payoffs.
        </Typography>
        <Box onDrop={handleDrop} onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
          sx={{ border: '2px dashed', borderColor: dragOver ? 'primary.main' : 'divider', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: dragOver ? alpha('#5b7fff', 0.06) : 'transparent', mb: 2 }}>
          <CloudUploadIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
          <Typography fontWeight={700} fontSize={13} mt={0.5}>Drop both custody files here</Typography>
          <Typography variant="caption" color="text.secondary">NT: Cash_Activity_Detail · JPM: All Transaction Activity</Typography>
        </Box>
        {[
          { key: 'nt', label: 'Northern Trust (NWL)', hint: 'Cash_Activity_Detail_w__Wire_Details.xls', file: ntFile, set: setNtFile },
          { key: 'jpm', label: 'JP Morgan (SUSA/SBLI)', hint: 'All Transaction Activity.csv', file: jpmFile, set: setJpmFile },
        ].map((item) => (
          <Card key={item.key} sx={{ mb: 1.5, borderColor: item.file ? 'primary.main' : 'divider' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, '&:last-child': { pb: 1.5 } }}>
              {item.file ? <CheckCircleIcon sx={{ color: 'primary.main' }} /> : <Box sx={{ width: 24, height: 24, border: '2px solid', borderColor: 'divider', borderRadius: '50%' }} />}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontSize={12} fontWeight={700}>{item.label}</Typography>
                <Typography fontSize={10} color={item.file ? 'primary.main' : 'text.secondary'}>{item.file ? item.file.name : item.hint}</Typography>
              </Box>
              <Button component="label" size="small" variant="outlined">
                {item.file ? 'Replace' : 'Browse'}
                <input type="file" accept=".xlsx,.xls,.csv" hidden onChange={(e) => { const f = e.target.files[0]; if (f) item.set(f); }} />
              </Button>
              {item.file && <IconButton size="small" onClick={() => item.set(null)}><CloseIcon fontSize="small" /></IconButton>}
            </CardContent>
          </Card>
        ))}
        {error && <Alert severity="warning" sx={{ mb: 1.5 }}>{error}</Alert>}
        <Button fullWidth variant="contained" disabled={(!ntFile && !jpmFile) || loading} onClick={onRunCashRec} sx={{ background: 'linear-gradient(135deg, #5b7fff, #3d5bd6)' }}>
          {loading ? 'Running Cash Rec…' : 'Run Cash Rec →'}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <StatusCards statuses={STATUS_CASH} statusOrder={STATUS_CASH_ORDER} counts={counts} total={results.length} active={filterStatus} onClick={(s) => setFilterStatus(filterStatus === s ? null : s)} />
      <Stack direction="row" spacing={1} mb={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField size="small" placeholder="Search broker or description…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 200 }} />
        <TextField size="small" select value={filterType} onChange={(e) => setFilterType(e.target.value)} sx={{ minWidth: 200 }}>
          <MenuItem value="All">Premiums + Payoffs</MenuItem>
          <MenuItem value="Premium">Premiums only</MenuItem>
          <MenuItem value="Payoff">Payoffs only</MenuItem>
        </TextField>
        <Button size="small" variant="outlined" onClick={() => { setResults(null); if (onResults) onResults({ cashResults: null, wires: {}, tradeWireIndex: {} }); }}>↩ New Files</Button>
        {(filterStatus || filterType !== 'All' || search) && (
          <Button size="small" color="error" variant="outlined" onClick={() => { setFilterStatus(null); setFilterType('All'); setSearch(''); }}>✕ Clear</Button>
        )}
      </Stack>
      <Box sx={{ height: 600, width: '100%' }}>
        <PamGrid rowData={filtered} columnDefs={columnDefs} onRowClicked={(e) => setSelected(e.data)} defaultColDef={{ sortable: true, resizable: true, filter: true, wrapHeaderText: true, autoHeaderHeight: true }} enableShowToolbar={false} />
      </Box>
      <CashDetailDialog selected={selected} cwTxRows={cwTxRows} onClose={() => setSelected(null)} />
    </Box>
  );
}
